
import React, { useMemo, useState, useEffect } from 'react';
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Popover,
  Popper,
  ClickAwayListener,
  ListItemIcon,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  AppsOutlined as AppsIcon,
  BarChartOutlined as DashboardIcon,
  CalendarMonthOutlined as CalendarIcon,
  DarkModeOutlined as DarkModeIcon,
  EditOutlined as EditIcon,
  GridViewOutlined as GridIcon,
  KeyboardArrowDownOutlined as ArrowDownIcon,
  LightModeOutlined as LightModeIcon,
  LogoutOutlined as LogoutIcon,
  NotificationsNoneOutlined as BellIcon,
  PeopleAltOutlined as UsersIcon,
  PushPinOutlined as PinIcon,
  PushPin as PinnedIcon,
  ReceiptLongOutlined as RtgsIcon,
  SearchOutlined as SearchIcon,
  SettingsOutlined as SettingsIcon,
  SecurityOutlined as RolesIcon,
  Assignment as TaskIcon,
  RestoreFromTrashOutlined as RestoreIcon,
  ChatBubbleOutlineOutlined as ChatIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, hasPageAccess } from '../context/AuthContext';
import { useColorMode } from '../context/ColorModeContext';
import { API_BASE_URL } from '../apiBase';

const sidebarCollapsedWidth = 72;
const sidebarExpandedWidth = 260;
const headerHeight = 72;

export function getAllSidebarPageItems() {
  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, to: '/dashboard', pageKey: 'dashboard' },
    { key: 'task_management', label: 'Task Management', icon: <TaskIcon />, to: '/tasks', pageKey: 'task_management' },
    { key: 'applications', label: 'Applications', icon: <AppsIcon />, to: '/applications', pageKey: 'applications' },
    { key: 'requests', label: 'Edit Requests', icon: <EditIcon />, to: '/edit-requests', pageKey: 'edit_requests' },
    { key: 'rtgs', label: 'RTGS', icon: <RtgsIcon />, to: '/rtgs', pageKey: 'rtgs' },
    { key: 'calendar', label: 'Calendar', icon: <CalendarIcon />, to: '/calendar', pageKey: 'calendar' },
    { key: 'head-office-report', label: 'Head Office Report', icon: <CalendarIcon />, to: '/head-office-report', pageKey: 'head_office_report' },
  ];
  const vendorMasterItems = [
    { key: 'articles', label: 'Article Creation', to: '/articles', icon: <EditIcon />, pageKey: 'article_creation' },
    { key: 'vendors', label: 'Vendor Management', to: '/vendors', icon: <UsersIcon />, pageKey: 'vendor_master' },
    { key: 'vendor-followup', label: 'Vendor Follow Up', to: '/vendor-followup', icon: <CalendarIcon />, pageKey: 'vendor_followup' },
    { key: 'vendor-followup-report', label: 'Vendor Follow Up Report', to: '/vendor-followup-report', icon: <DashboardIcon />, pageKey: 'vendor_followup_report' },
    { key: 'manual-head-office-transaction', label: 'Manual Head Office Transaction', to: '/manual-head-office-transaction', icon: <RtgsIcon />, pageKey: 'manual_head_office_transaction' },
    { key: 'head-office-report', label: 'Daily Head Office Transaction Report', to: '/head-office-report', icon: <DashboardIcon />, pageKey: 'daily_head_office_transaction_report' },
    { key: 'hardcopy-sheet', label: 'Hardcopy Sheet Management', to: '/hardcopy-sheet', icon: <GridIcon />, pageKey: 'hardcopy_sheet_management' },
    { key: 'hardcopy-sheet-report', label: 'Hardcopy Sheet Report', to: '/hardcopy-sheet-report', icon: <DashboardIcon />, pageKey: 'hardcopy_sheet_report' },
    { key: 'multi-hardcopy-sheet-report', label: 'Multi Hardcopy Sheet Report', to: '/multi-hardcopy-sheet-report', icon: <DashboardIcon />, pageKey: 'multi_hardcopy_sheet_report' },
    { key: 'code-purpose', label: 'Code to Code Purpose', to: '/code-purpose', icon: <EditIcon />, pageKey: 'code_to_code_purpose' },
    { key: 'transaction-types', label: 'Transaction Types', to: '/transaction-types', icon: <SettingsIcon />, pageKey: 'transaction_types' },
    { key: 'applications', label: 'User Application Report', to: '/applications', icon: <AppsIcon />, pageKey: 'applications' },
    { key: 'applications-add', label: 'User Application Form', to: '/applications/add', icon: <AddIcon />, pageKey: 'applications_add' },
  ];
  const rolesItems = [
    { key: 'users', label: 'Create User', to: '/users', icon: <UsersIcon />, pageKey: 'users' },
    { key: 'role_master', label: 'Role Master', to: '/rolemaster', icon: <RolesIcon />, pageKey: 'role_master' },
    { key: 'user_permissions', label: 'User Permissions', to: '/user-permissions', icon: <UsersIcon />, pageKey: 'user_permissions' },
    { key: 'page_master', label: 'Page Master', to: '/pagemaster', icon: <SettingsIcon />, pageKey: 'page_master' },
  ];
  const extraItems = [
    { key: 'datacenter', label: 'Datacenter', icon: <SettingsIcon />, to: '/datacenter', pageKey: 'datacenter' },
    { key: 'settings', label: 'Settings', icon: <SettingsIcon />, to: '/settings', pageKey: 'settings' },
  ];
  
  const all = [...navItems, ...vendorMasterItems, ...rolesItems, ...extraItems];
  const seen = new Set();
  const unique = [];
  for (const item of all) {
    const k = item.to;
    if (seen.has(k)) continue;
    seen.add(k);
    unique.push(item);
  }
  return unique;
}

export default function Layout({ children, title = 'Dashboard' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useColorMode();

  const [profileAnchor, setProfileAnchor] = useState(null);
  const profileOpen = Boolean(profileAnchor);
  const [navHoverOpen, setNavHoverOpen] = useState(false);
  const [navPinnedOpen, setNavPinnedOpen] = useState(false);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const searchInputRef = React.useRef(null);

  const displayName = useMemo(() => {
    // Show application_code if available, otherwise fallback to email username
    if (user?.application_code) {
      return user.application_code;
    }
    const email = user?.email || '';
    const base = email.split('@')[0] || 'User';
    return base;
  }, [user?.application_code, user?.email]);

  // Load notifications
  const loadNotifications = async () => {
    if (!user) return;
    
    try {
      setLoadingNotifications(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/api/notifications?limit=20`, { headers });
      const data = await res.json();
      
      if (res.ok) {
        setNotifications(data.notifications || []);
        setNotificationCount(data.unread_count || 0);
      }
    } catch (e) {
      console.error('Failed to load notifications:', e);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Load unread count only (lighter request)
  const loadUnreadCount = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/api/notifications/unread-count`, { headers });
      const data = await res.json();
      
      if (res.ok) {
        setNotificationCount(data.unread_count || 0);
      }
    } catch (e) {
      console.error('Failed to load unread count:', e);
    }
  };

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    
    // Poll for notifications every 30 seconds
    const interval = setInterval(() => {
      loadUnreadCount();
      if (notificationAnchor) {
        loadNotifications();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [user, notificationAnchor]);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        await fetch(`${API_BASE_URL}/api/notifications/${notification.id}/read`, {
          method: 'POST',
          headers,
        });
        loadNotifications();
      } catch (e) {
        console.error('Failed to mark notification as read:', e);
      }
    }

    if (notification.link) {
      navigate(notification.link);
      setNotificationAnchor(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers,
      });
      loadNotifications();
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  };

  const formatNotificationTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // Admin-only items (needs user context)
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin';
  const adminItems = useMemo(
    () => (isSuperAdmin || isAdmin) ? [
      { key: 'login-history', label: 'Login History', icon: <SettingsIcon />, to: '/login-history', pageKey: null },
    ] : [],
    [isSuperAdmin, isAdmin]
  );

  const navItems = useMemo(
    () => [
      { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, to: '/dashboard', pageKey: 'dashboard' },
      { key: 'chat', label: 'Chat', icon: <ChatIcon />, to: '/chat', pageKey: null },
      { key: 'task_management', label: 'Task Management', icon: <TaskIcon />, to: '/tasks', pageKey: 'task_management' },
      { key: 'applications', label: 'Applications', icon: <AppsIcon />, to: '/applications', pageKey: 'applications' },
      { key: 'requests', label: 'Edit Requests', icon: <EditIcon />, to: '/edit-requests', pageKey: 'edit_requests' },
      { key: 'rtgs', label: 'RTGS', icon: <RtgsIcon />, to: '/rtgs', pageKey: 'rtgs' },
      { key: 'calendar', label: 'Calendar', icon: <CalendarIcon />, to: '/calendar', pageKey: 'calendar' },
      { key: 'head-office-report', label: 'Head Office Report', icon: <CalendarIcon />, to: '/head-office-report', pageKey: 'head_office_report' },
      ...adminItems,
    ],
    [adminItems]
  );

  // Get all searchable pages
  const allSearchablePages = useMemo(() => {
    const allPages = getAllSidebarPageItems();
    // Filter pages based on user permissions
    return allPages
      .filter((item) => !item.pageKey || hasPageAccess(user, item.pageKey))
      .map((item) => ({
        ...item,
        searchText: `${item.label} ${item.to}`.toLowerCase(),
      }));
  }, [user]);

  // Filter pages based on search query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().trim();
    return allSearchablePages
      .filter((page) => page.searchText.includes(query))
      .slice(0, 10); // Limit to 10 results
  }, [searchQuery, allSearchablePages]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim()) {
      setSearchAnchorEl(searchInputRef.current);
    } else {
      setSearchAnchorEl(null);
    }
  };

  const handleSearchItemClick = (to) => {
    setSearchQuery('');
    setSearchAnchorEl(null);
    goTo(to);
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
  };

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  const navOpen = navPinnedOpen || navHoverOpen;

  const goTo = (to) => {
    navigate(to);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [rolesHoverHeader, setRolesHoverHeader] = useState(false);
  const [rolesHoverList, setRolesHoverList] = useState(false);
  const rolesItems = useMemo(
    () => [
      { key: 'users', label: 'Create User', to: '/users', icon: <UsersIcon />, pageKey: 'users' },
      { key: 'role_master', label: 'Role Master', to: '/rolemaster', icon: <RolesIcon />, pageKey: 'role_master' },
      { key: 'user_permissions', label: 'User Permissions', to: '/user-permissions', icon: <UsersIcon />, pageKey: 'user_permissions' },
      { key: 'page_master', label: 'Page Master', to: '/pagemaster', icon: <SettingsIcon />, pageKey: 'page_master' },
    ],
    []
  );

  const [vendorMasterHoverHeader, setVendorMasterHoverHeader] = useState(false);
  const [vendorMasterHoverList, setVendorMasterHoverList] = useState(false);
  const vendorMasterItems = useMemo(
    () => [
      { key: 'articles', label: 'Article Creation', to: '/articles', icon: <EditIcon />, pageKey: 'article_creation' },
      { key: 'vendors', label: 'Vendor Management', to: '/vendors', icon: <UsersIcon />, pageKey: 'vendor_master' },
      { key: 'vendor-followup', label: 'Vendor Follow Up', to: '/vendor-followup', icon: <CalendarIcon />, pageKey: 'vendor_followup' },
      { key: 'vendor-followup-report', label: 'Vendor Follow Up Report', to: '/vendor-followup-report', icon: <DashboardIcon />, pageKey: 'vendor_followup_report' },
      { key: 'manual-head-office-transaction', label: 'Manual Head Office Transaction', to: '/manual-head-office-transaction', icon: <RtgsIcon />, pageKey: 'manual_head_office_transaction' },
      { key: 'head-office-report', label: 'Daily Head Office Transaction Report', to: '/head-office-report', icon: <DashboardIcon />, pageKey: 'daily_head_office_transaction_report' },
      { key: 'hardcopy-sheet', label: 'Hardcopy Sheet Management', to: '/hardcopy-sheet', icon: <GridIcon />, pageKey: 'hardcopy_sheet_management' },
      { key: 'hardcopy-sheet-report', label: 'Hardcopy Sheet Report', to: '/hardcopy-sheet-report', icon: <DashboardIcon />, pageKey: 'hardcopy_sheet_report' },
      { key: 'multi-hardcopy-sheet-report', label: 'Multi Hardcopy Sheet Report', to: '/multi-hardcopy-sheet-report', icon: <DashboardIcon />, pageKey: 'multi_hardcopy_sheet_report' },
      { key: 'code-purpose', label: 'Code to Code Purpose', to: '/code-purpose', icon: <EditIcon />, pageKey: 'code_to_code_purpose' },
      { key: 'transaction-types', label: 'Transaction Types', to: '/transaction-types', icon: <SettingsIcon />, pageKey: 'transaction_types' },
      { key: 'applications', label: 'User Application Report', to: '/applications', icon: <AppsIcon />, pageKey: 'applications' },
      { key: 'applications-add', label: 'User Application Form', to: '/applications/add', icon: <AddIcon />, pageKey: 'applications_add' },
    ],
    []
  );

  const [restorePointHoverHeader, setRestorePointHoverHeader] = useState(false);
  const [restorePointHoverList, setRestorePointHoverList] = useState(false);
  const restorePointItems = useMemo(
    () => [
      { key: 'restore_daily_vendor_followup', label: 'Daily Vendor Follow Up Report', to: '/restore/daily-vendor-followup-report', icon: <RestoreIcon /> },
      { key: 'restore_articles', label: 'Articles', to: '/restore/articles', icon: <RestoreIcon /> },
      { key: 'restore_vendors', label: 'Vendor Management', to: '/restore/vendors', icon: <RestoreIcon /> },
      { key: 'restore_daily_head_office', label: 'Daily Head Office Transaction Report', to: '/restore/daily-head-office-transaction-report', icon: <RestoreIcon /> },
      { key: 'restore_hardcopy_sheet', label: 'Hardcopy Sheet Report', to: '/restore/hardcopy-sheet-report', icon: <RestoreIcon /> },
      { key: 'restore_multi_hardcopy_sheet', label: 'Multi Hardcopy Sheet Report', to: '/restore/multi-hardcopy-sheet-report', icon: <RestoreIcon /> },
    ],
    []
  );

  const adminItemsAvailable =
    hasPageAccess(user, 'users') ||
    hasPageAccess(user, 'role_master') ||
    hasPageAccess(user, 'user_permissions') ||
    hasPageAccess(user, 'page_master') ||
    hasPageAccess(user, 'datacenter') ||
    user?.role === 'superadmin';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex' }}>
      {/* Sidebar (sticky) */}
      <Box
        component="aside"
        sx={{
          width: navOpen ? sidebarExpandedWidth : sidebarCollapsedWidth,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          height: '100vh',
          position: 'sticky',
          top: 0,
          overflowY: 'auto',
          transition: 'width 160ms ease',
          // Hide scrollbar for cleaner look
          '&::-webkit-scrollbar': { width: 0 },
        }}
        onMouseEnter={() => setNavHoverOpen(true)}
        onMouseLeave={() => setNavHoverOpen(false)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            py: 2,
            gap: 0.5,
            height: '100%',
          }}
        >
          {/* Pin / unpin toggle */}
          <Box sx={{ px: 1.5, display: 'flex', justifyContent: navOpen ? 'flex-end' : 'center' }}>
            <Tooltip title={navPinnedOpen ? 'Unpin menu' : 'Pin menu'} placement="right">
              <IconButton onClick={() => setNavPinnedOpen((v) => !v)} size="small">
                {navPinnedOpen ? <PinnedIcon /> : <PinIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {(() => {
            const splitIndex = navItems.findIndex((it) => it.key === 'dashboard');
            const before = splitIndex >= 0 ? navItems.slice(0, splitIndex + 1) : [];
            const after = splitIndex >= 0 ? navItems.slice(splitIndex + 1) : navItems;

            const visibleBefore = before.filter((it) => !it.pageKey || hasPageAccess(user, it.pageKey || it.key));
            const visibleAfter = after.filter((it) => !it.pageKey || hasPageAccess(user, it.pageKey || it.key));

            const renderItem = (item) => {
              const active = isActive(item.to);
              const row = (
                <Box
                  onClick={() => goTo(item.to)}
                  sx={{
                  mx: 1,
                  px: navOpen ? 1.5 : 0,
                  height: 44,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: navOpen ? 'flex-start' : 'center',
                  gap: navOpen ? 1.25 : 0,
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                  bgcolor: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                  color: active ? 'secondary.main' : 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                  }}
                >
                  <Box sx={{ width: 36, display: 'grid', placeItems: 'center' }}>{item.icon}</Box>
                  {navOpen && <Typography sx={{ fontSize: 14 }}>{item.label}</Typography>}
                </Box>
              );

              return navOpen ? (
                <Box key={item.key}>{row}</Box>
              ) : (
                <Tooltip key={item.key} title={item.label} placement="right">
                  {row}
                </Tooltip>
              );
            };

            return (
              <>
                {visibleBefore.map(renderItem)}



                {(() => {
                  const visibleVendorItems = vendorMasterItems.filter((it) =>
                    hasPageAccess(user, it.pageKey || it.key),
                  );
                  if (!visibleVendorItems.length) return null;

                  const vendorMasterOpen = vendorMasterHoverHeader || vendorMasterHoverList;
                  const header = (
                    <Box
                      onMouseEnter={() => setVendorMasterHoverHeader(true)}
                      onMouseLeave={() => {
                        // Small delay to allow moving to the list
                        setTimeout(() => setVendorMasterHoverHeader(false), 100);
                      }}
                      sx={{
                        mx: 1,
                        px: navOpen ? 1.5 : 0,
                        height: 44,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: navOpen ? 'space-between' : 'center',
                        gap: navOpen ? 1.25 : 0,
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        bgcolor: 'transparent',
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ width: 36, display: 'grid', placeItems: 'center' }}>
                          <AppsIcon />
                        </Box>
                        {navOpen && <Typography sx={{ fontSize: 14 }}>Vendor Master</Typography>}
                      </Box>
                      {navOpen && (
                        <ArrowDownIcon
                          sx={{
                            fontSize: 18,
                            transform: vendorMasterOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms ease',
                          }}
                        />
                      )}
                    </Box>
                  );

                  return navOpen ? header : <Tooltip title="Vendor Master" placement="right">{header}</Tooltip>;
                })()}
                {navOpen && (vendorMasterHoverHeader || vendorMasterHoverList) && (
                  <Box
                    onMouseEnter={() => {
                      setVendorMasterHoverList(true);
                      setVendorMasterHoverHeader(true); // Keep header active
                    }}
                    onMouseLeave={() => {
                      setVendorMasterHoverList(false);
                      setVendorMasterHoverHeader(false);
                    }}
                    sx={{ mx: 1, mt: 0.5, mb: 1 }}
                  >
                    {vendorMasterItems
                      .filter((it) => hasPageAccess(user, it.pageKey || it.key))
                      .map((it) => {
                      const active = isActive(it.to);
                      const row = (
                        <Box
                          key={it.key}
                          onClick={() => goTo(it.to)}
                          sx={{
                            px: 1.5,
                            height: 36,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            bgcolor: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                            color: active ? 'secondary.main' : 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                          }}
                        >
                          <Box sx={{ width: 28, display: 'grid', placeItems: 'center', mr: 1 }}>
                            {it.icon}
                          </Box>
                          <Typography sx={{ fontSize: 13 }}>{it.label}</Typography>
                        </Box>
                      );
                      const extraSpaceKeys = ['manual-head-office-transaction', 'head-office-report', 'hardcopy-sheet'];
                      const marginBottom = extraSpaceKeys.includes(it.key) ? 2 : 0.5;
                      return <Box key={it.key} sx={{ ml: 4, mb: marginBottom }}>{row}</Box>;
                      })}
                  </Box>
                )}

                {visibleAfter.map(renderItem)}

                {navOpen && adminItemsAvailable && (
                  <Typography
                    variant="overline"
                    sx={{
                      mx: 3,
                      mt: 2,
                      mb: 1,
                      display: 'block',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                    }}
                  >
                    Admin
                  </Typography>
                )}

                {(() => {
                  const visibleRolesItems = rolesItems.filter((it) =>
                    hasPageAccess(user, it.pageKey || it.key),
                  );
                  if (!visibleRolesItems.length) return null;

                  const rolesOpen = rolesHoverHeader || rolesHoverList;
                  const header = (
                    <Box
                      onMouseEnter={() => setRolesHoverHeader(true)}
                      onMouseLeave={() => {
                        setTimeout(() => setRolesHoverHeader(false), 100);
                      }}
                      sx={{
                        mx: 1,
                        px: navOpen ? 1.5 : 0,
                        height: 44,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: navOpen ? 'space-between' : 'center',
                        gap: navOpen ? 1.25 : 0,
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        bgcolor: 'transparent',
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ width: 36, display: 'grid', placeItems: 'center' }}>
                          <RolesIcon />
                        </Box>
                        {navOpen && <Typography sx={{ fontSize: 14 }}>Roles</Typography>}
                      </Box>
                      {navOpen && (
                        <ArrowDownIcon
                          sx={{
                            fontSize: 18,
                            transform: rolesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms ease',
                          }}
                        />
                      )}
                    </Box>
                  );
                  return navOpen ? header : <Tooltip title="Roles" placement="right">{header}</Tooltip>;
                })()}
                {navOpen && (rolesHoverHeader || rolesHoverList) && (
                  <Box
                    onMouseEnter={() => {
                      setRolesHoverList(true);
                      setRolesHoverHeader(true);
                    }}
                    onMouseLeave={() => {
                      setRolesHoverList(false);
                      setRolesHoverHeader(false);
                    }}
                    sx={{ mx: 1, mt: 0.5 }}
                  >
                    {rolesItems
                      .filter((it) => hasPageAccess(user, it.pageKey || it.key))
                      .map((it) => {
                      const active = isActive(it.to);
                      const row = (
                        <Box
                          key={it.key}
                          onClick={() => goTo(it.to)}
                          sx={{
                            px: 1.5,
                            height: 36,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            bgcolor: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                            color: active ? 'secondary.main' : 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                          }}
                        >
                          <Box sx={{ width: 28, display: 'grid', placeItems: 'center', mr: 1 }}>
                            {it.icon}
                          </Box>
                          <Typography sx={{ fontSize: 13 }}>{it.label}</Typography>
                        </Box>
                      );
                      return <Box key={it.key} sx={{ ml: 4 }}>{row}</Box>;
                      })}
                  </Box>
                )}
                {hasPageAccess(user, 'datacenter') &&
                  [
                    {
                      key: 'datacenter',
                      label: 'Datacenter',
                      icon: <SettingsIcon />,
                      to: '/datacenter',
                      pageKey: 'datacenter',
                    },
                  ].map(renderItem)}

                {user?.role === 'superadmin' && (() => {
                  const active = isActive('/restore');
                  const row = (
                    <Box
                      onClick={() => goTo('/restore')}
                      sx={{
                        mx: 1,
                        px: navOpen ? 1.5 : 0,
                        height: 44,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: navOpen ? 'flex-start' : 'center',
                        gap: navOpen ? 1.25 : 0,
                        cursor: 'pointer',
                        color: active ? 'secondary.main' : 'text.secondary',
                        bgcolor: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      }}
                    >
                      <Box sx={{ width: 36, display: 'grid', placeItems: 'center' }}>
                        <RestoreIcon />
                      </Box>
                      {navOpen && <Typography sx={{ fontSize: 14 }}>Restore Point</Typography>}
                    </Box>
                  );
                  return navOpen ? row : <Tooltip title="Restore Point" placement="right">{row}</Tooltip>;
                })()}
              </>
            );
          })()}

          <Box sx={{ flexGrow: 1 }} />

          {(() => {
            const active = isActive('/settings');
            const row = (
              <Box
                onClick={() => goTo('/settings')}
                sx={{
                  mx: 1,
                  px: navOpen ? 1.5 : 0,
                  height: 44,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: navOpen ? 'flex-start' : 'center',
                  gap: navOpen ? 1.25 : 0,
                  cursor: 'pointer',
                  color: active ? 'secondary.main' : 'text.secondary',
                  bgcolor: active ? 'rgba(124,58,237,0.12)' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                }}
              >
                <Box sx={{ width: 36, display: 'grid', placeItems: 'center' }}>
                  <SettingsIcon />
                </Box>
                {navOpen && <Typography sx={{ fontSize: 14 }}>Settings</Typography>}
              </Box>
            );
            return navOpen ? row : <Tooltip title="Settings" placement="right">{row}</Tooltip>;
          })()}

          <Box sx={{ px: 1.5, display: 'flex', justifyContent: navOpen ? 'flex-start' : 'center', pb: 0.5 }}>
            <Tooltip title="New" placement="right">
              <Box
                onClick={() => goTo('/new')}
                sx={{
                  width: navOpen ? '100%' : 52,
                  height: 52,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: navOpen ? 1 : 0,
                  cursor: 'pointer',
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  boxShadow: '0 12px 22px rgba(124,58,237,0.22)',
                  '&:hover': { bgcolor: 'secondary.dark' },
                }}
              >
                <AddIcon />
                {navOpen && <Typography sx={{ fontSize: 14 }}>New</Typography>}
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Main column */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      {/* Topbar */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          height: headerHeight,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>

        <Box sx={{ position: 'relative', display: { xs: 'none', md: 'block' }, width: { xs: '100%', sm: 400 }, maxWidth: 500 }}>
          <Paper
            component="form"
            elevation={0}
            onSubmit={(e) => {
              e.preventDefault();
              if (searchResults.length > 0) {
                handleSearchItemClick(searchResults[0].to);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 2,
              py: 1,
              width: '100%',
              borderRadius: 2.5,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6'),
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <InputBase
              inputRef={searchInputRef}
              placeholder="Search pages..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={(e) => {
                if (searchQuery.trim()) {
                  setSearchAnchorEl(e.currentTarget);
                }
              }}
              sx={{ fontSize: 14, width: '100%' }}
            />
          </Paper>

          {/* Search Results Popper */}
          {searchAnchorEl && (
            <Popper
              open={Boolean(searchAnchorEl && searchResults.length > 0)}
              anchorEl={searchAnchorEl}
              placement="bottom-start"
              style={{ zIndex: 1300, width: searchAnchorEl?.offsetWidth || 400 }}
            >
              <ClickAwayListener onClickAway={handleSearchClose}>
                <Paper
                  elevation={8}
                  sx={{
                    width: '100%',
                    maxHeight: 400,
                    overflow: 'auto',
                    mt: 0.5,
                    borderRadius: 2,
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {searchResults.map((page, index) => (
                      <React.Fragment key={page.to || page.key || index}>
                        <ListItem
                          button
                          onClick={() => handleSearchItemClick(page.to)}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                            {page.icon || <DashboardIcon />}
                          </ListItemIcon>
                          <ListItemText
                            primary={page.label}
                            secondary={page.to}
                            primaryTypographyProps={{ fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                        {index < searchResults.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </ClickAwayListener>
            </Popper>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Notifications">
            <IconButton 
              sx={{ mr: 1 }}
              onClick={(e) => {
                setNotificationAnchor(e.currentTarget);
                loadNotifications();
              }}
            >
              <Badge badgeContent={notificationCount > 0 ? notificationCount : null} color="error">
                <BellIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Notification Popover */}
          <Popover
            open={Boolean(notificationAnchor)}
            anchorEl={notificationAnchor}
            onClose={() => setNotificationAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                width: 380,
                maxHeight: 500,
                mt: 1,
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Notifications</Typography>
                {notificationCount > 0 && (
                  <Button size="small" onClick={handleMarkAllAsRead}>
                    Mark all read
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {loadingNotifications ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">Loading...</Typography>
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No notifications</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {notifications.map((notif, index) => (
                    <React.Fragment key={notif.id}>
                      <ListItem
                        button
                        onClick={() => handleNotificationClick(notif)}
                        sx={{
                          bgcolor: notif.is_read ? 'transparent' : 'action.selected',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: notif.is_read ? 400 : 600 }}
                            >
                              {notif.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {notif.message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatNotificationTime(notif.created_at)}
                              </Typography>
                            </Box>
                          }
                        />
                        {!notif.is_read && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              ml: 1,
                            }}
                          />
                        )}
                      </ListItem>
                      {index < notifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Popover>

          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton onClick={toggleMode} sx={{ mr: 1 }}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          <Box
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              cursor: 'pointer',
              pl: 1.25,
              pr: 1,
              py: 0.75,
              borderRadius: 3,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main' }}>
              {(user?.application_code?.[0] || displayName?.[0] || 'U').toUpperCase()}
            </Avatar>
            <Box sx={{ lineHeight: 1.1 }}>
              <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{displayName}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                {user?.email || '—'}
              </Typography>
            </Box>
            <ArrowDownIcon sx={{ color: 'text.secondary' }} />
          </Box>
        </Box>

        <Menu
          anchorEl={profileAnchor}
          open={profileOpen}
          onClose={() => setProfileAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setProfileAnchor(null);
              handleLogout();
            }}
            sx={{ gap: 1 }}
          >
            <LogoutIcon fontSize="small" />
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {/* Content */}
      <Box component="main" sx={{ p: 3 }}>
        {children}
      </Box>
      </Box>
    </Box>
  );
}
